import colorfn from 'color';
import { CSSProperties } from 'react';
import { increaseIntensivity, decreaseIntensivity, grayscaleCompatible } from './utils/colors';

export const pageLayout = {
  sidebarWidth: '285px',
  sidebarWidthXxlg: '455px',
  rightWidth: '',
  rightMargin: '',
  tocWidth: '285px',
  tocWidthXlg: '405px',
  tocWidthXxlg: '625px'
};

export const transitions = {
  hover: 'all .25s cubic-bezier(0.25, 0.8, 0.25, 1) 0s',
  hoverFast: 'all .15s cubic-bezier(0.25, 0.8, 0.25, 1) 0s',
  hoverColor: 'color .25s cubic-bezier(0.25, 0.8, 0.25, 1) 0s'
};

const content = (colors) => ({
  background: colors.darkerBackground,
  border: colors.border,
  font: colors.font,
  titleFont: increaseIntensivity(colors.font, 0.15),
  code: {
    border: colors.border,
    font: colors.fontDark,
    background: colors.background
  }
});

const navigationSidebar = (colors) => ({
  backgroundSecondary: colors.background,
  backgroundPrimary: colors.background,
  border: colors.border,
  row: {
    hover: colors.border,
    active: colorfn(colors.white).mix(colorfn(colors.background)).whiten(3.5).hex(),
    activeBorder: colors.border,
    collapseHover: colors.hover
  },
  font: {
    group: decreaseIntensivity(colors.font, 0.25),
    base: colors.font,
    nested: decreaseIntensivity(colors.font, 0.25),
    active: colors.primary,
    hover: colors.primary
  },
  poweredBy: {
    font: colors.grayLight,
    background: colors.border,
    hover: colors.primary
  }
});

const header = (colors) => ({
  background: colors.background,
  shadow: colors.shadow,
  font: {
    base: colors.primary,
    hover: colorfn(colors.primary).hex()
  },
  border: colors.border,
  icons: {
    background: colors.background,
    shadow: colors.shadow,
    fill: decreaseIntensivity(colors.background, 0.4),
    stroke: decreaseIntensivity(colors.background, 0.4),
    hover: colors.primary
  }
});

const search = (colors) => ({
  background: colors.background,
  mark: {
    font: colors.font,
    background: colors.primary
  },
  font: {
    base: colors.font,
    hover: colors.font,
    highlight: colors.fontDark
  },
  hover: colors.border,
  border: colors.border,
  pagination: {
    background: colors.darkerBackground,
    border: colors.border,
    font: colors.font,
    hover: colors.border,
    current: {
      background: colors.primary,
      font: grayscaleCompatible(colors.primary)
    }
  }
});

const editOnRepo = (colors) => ({
  background: colors.background,
  border: colors.border,
  hover: colors.hover,
  font: {
    base: colors.font,
    hover: grayscaleCompatible(colors.hover)
  }
});

const jargon = (colors) => ({
  background: colors.background,
  border: colors.border,
  font: colors.font,
  shadow: colors.shadow
});

const highlights = (colors) => ({
  warning: {
    border: colors.orange,
    background: colors.orangeLight,
    font: colors.fontDark
  },
  error: {
    border: colors.red,
    background: colors.redLight,
    font: colors.fontDark
  },
  info: {
    border: colors.blue,
    background: colors.blueLight,
    font: colors.fontDark
  },
  'subtle-info': {
    border: colors.grayLight,
    background: 'none',
    font: colors.fontLight
  },
  tip: {
    border: colors.green,
    background: colors.greenLight,
    font: colors.fontDark
  }
});

const table = (colors) => ({
  header: {
    background: colors.primary,
    font: increaseIntensivity(colorfn(colors.primary).negate().grayscale(), 0.5)
  },
  oddRow: colors.darkerBackground,
  evenRow: colors.background,
  rowHover: `${colors.hover}3d`,
  border: colors.border
});

const tableOfContents = (colors) => ({
  background: colors.darkerBackground,
  font: {
    base: decreaseIntensivity(colors.font, 0.15),
    hover: colors.primary,
    current: colors.primary
  },
  border: colors.border
});

const previousNext = (colors) => ({
  background: colors.darkerBackground,
  hover: colors.primary,
  font: colors.font,
  fontLabel: decreaseIntensivity(colors.font, 0.45),
  border: colors.border,
  shadow: colors.shadow
});

// $primary: #1F1F1F;

// $font-color-primary: #E8E8E8;
// $font-color-secondary: #00939c;
// $font-color-ternary: #848484;
// $scrollbar-color: #131313;

// $yaml-primary: #577da5;
// $yaml-secondary: #ad8372;

// $error-color: #dc3545;
// $success-color: $font-color-secondary;

// $box-shadow: 4px 4px 10px rgba(26,26,26,1), -4px -4px 10px rgba(53,53,53,.25);
// $box-shadow-inset: inset 4px 4px 10px rgba(26,26,26,1), inset -4px -4px 10px rgba(53,53,53,.25);

const blue = '#0066CC';
const blueDark = '#264D99';
const gray = '#656565';
const stacktapeGreen = '#00939c';

export const headerHeight = '70px';

export const colors = {
  blue,
  blueDark,
  gray,
  blueLight: '#CDDFF5',
  red: '#E40046',
  redLight: '#FAD0DD',
  violet: '#A05EB5',
  green: '#00965E',
  greenLight: '#D0EBE1',
  yellow: '#FFC72C',
  orange: '#ED8B00',
  orangeLight: '#FBE9D0',
  white: '#FFFFFF',
  black: '#000000',
  grayLight: '#AEAEAE',
  grayDark: '#3B4656',
  primary: '#00939c',
  primaryDark: stacktapeGreen,
  primaryDark2: '#046166',
  font: '#E8E8E8',
  fontDark: '#121213',
  background: '#252525',
  darkerBackground: '#1F1F1F',
  sidebarsSeparator: '#1F1F1F',
  lightGray: '#a6a5a5',
  border: '#636363',
  hover: stacktapeGreen,
  shadow: '#0c0c0c',
  highlightedCodeLine: 'rgb(53, 59, 69)',
  badgeHoverBackground: '#379fef',
  warnColor: '#e5ab13'
};

export const effects = {
  glow: {
    boxShadow: `0px 0px 35px -6px ${colors.primary}`,
    textShadow: `0 0 10px ${colors.primary}, 0 0 12px ${colors.primary}, 0 0 18px ${colors.primary}`
  }
};

export const shadows = {
  boxShadow: `1px 1px 1.5px ${colors.shadow}, -1px -1px 1.5px ${colors.shadow};`,
  boxShadowConfigurableColors: (color: string) => `1px 1px 1.5px ${color}, -1px -1px 1.5px ${color};`
};

export const textShadows = {
  softOutline: `
    -0.35px -0.35px 0 #000,
     0   -0.35px 0 #000,
     0.35px -0.35px 0 #000,
     0.35px  0   0 #000,
     0.35px  0.35px 0 #000,
     0    0.35px 0 #000,
    -0.35px  0.35px 0 #000,
    -0.35px  0   0 #000;
  `,
  softerOutline: `
  -0.25px -0.25px 0 #000,
   0   -0.25px 0 #000,
   0.25px -0.25px 0 #000,
   0.25px  0   0 #000,
   0.25px  0.25px 0 #000,
   0    0.25px 0 #000,
  -0.25px  0.25px 0 #000,
  -0.25px  0   0 #000;
`
};

export const fancyBorder: CSSProperties = {
  borderRadius: '6px',
  boxShadow: shadows.boxShadow,
  border: `1px solid ${colors.background}`
};

export const prettyScrollBar = {
  '&::-webkit-scrollbar-track': {
    height: '9px',
    width: '9px',
    WebkitBoxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
    backgroundColor: 'white'
  },
  '&::-webkit-scrollbar': {
    height: '9px',
    width: '9px',
    backgroundColor: 'white'
  },
  '&::-webkit-scrollbar-thumb': {
    height: '9px',
    width: '9px',
    WebkitBoxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
    backgroundColor: '#45484d'
  }
};

export const theme = {
  colors,
  layout: pageLayout,
  transitions,
  header: header(colors),
  search: search(colors),
  navigationSidebar: navigationSidebar(colors),
  content: content(colors),
  editOnRepo: editOnRepo(colors),
  jargon: jargon(colors),
  highlights: highlights(colors),
  table: table(colors),
  tableOfContents: tableOfContents(colors),
  previousNext: previousNext(colors)
};

// const colors = {
//   ...defaultColors,
//   primary: defaultColors.red,
//   primaryDark: defaultColors.blueDark,
//   fontLight: '#efefef',
//   font: '#dddddd',
//   fontDark: '#8a8a8a',
//   background: '#29282A',
//   mainBackground: '#1E1E1F',
//   border: '#323234',
//   hover: defaultColors.red,
//   shadow: `${defaultColors.gray}33`
// };
